import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { StatusFilterTabs } from '../components/StatusFilterTabs.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel, contractorTypeOptions } from '../lib/companyInfo.ts';
import { teamRegionNames, useTeamRegions } from '../lib/teamRegions.ts';
import {
  configuredFieldValues,
  countByStatus,
  filterCompanies,
  parseFieldFilterParam,
  parseStatusFilterParam,
  type StatusFilter,
} from '../lib/companyFilter.ts';
import type { Company } from '../lib/types.ts';

export function AdminCompanies() {
  const qc = useQueryClient();
  const { activeTeamRegion } = useAdminAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['companies', { region: activeTeamRegion }],
    queryFn: () =>
      adminApi.get<{ companies: Company[] }>(
        activeTeamRegion
          ? `/api/companies?team_region=${encodeURIComponent(activeTeamRegion)}`
          : '/api/companies',
      ),
  });

  const { branding } = useBranding();
  const regionsQuery = useTeamRegions();

  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = parseStatusFilterParam(searchParams.get('status'));
  const setStatusFilter = (next: StatusFilter) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('status');
        else p.set('status', next);
        return p;
      },
      { replace: true },
    );
  };

  // Contractor type and team region options are curated in Settings
  // (branding) — same source as the create-company modal, in configured order.
  const typeValues = useMemo(
    () =>
      configuredFieldValues(
        contractorTypeOptions(branding),
        data?.companies ?? [],
        'contractor_type',
      ),
    [branding, data],
  );
  const regionValues = useMemo(
    () =>
      configuredFieldValues(
        teamRegionNames(regionsQuery.data),
        data?.companies ?? [],
        'team_region',
      ),
    [regionsQuery.data, data],
  );

  // Field filters are multi-select: each URL key is repeatable
  // (?type=a&type=b&region=x). Read every value with getAll and
  // validate against the current option pool.
  const typeFilter = parseFieldFilterParam(searchParams.getAll('type'), typeValues);
  const regionFilter = parseFieldFilterParam(searchParams.getAll('region'), regionValues);

  // ABN verification filter: ?abn=verified | unverified | (absent = any)
  const abnRaw = searchParams.get('abn');
  const abnVerified: 'verified' | 'unverified' | null =
    abnRaw === 'verified' || abnRaw === 'unverified' ? abnRaw : null;

  // Rewrite a repeatable field-filter param: drop every existing entry for the
  // key, then append one per selected value (empty selection = no key).
  const setFieldFilter = (key: 'type' | 'region', next: string[]) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete(key);
        for (const v of next) p.append(key, v);
        return p;
      },
      { replace: true },
    );
  };

  const companies = useMemo(
    () =>
      filterCompanies(data?.companies ?? [], {
        query: filter,
        status: statusFilter,
        contractorType: typeFilter,
        teamRegion: regionFilter,
        abnVerified,
      }),
    [data, filter, statusFilter, typeFilter, regionFilter, abnVerified],
  );

  // Status pill counts are faceted: they reflect every active filter EXCEPT
  // status itself (a facet never filters its own counts — otherwise clicking a
  // pill would zero all the others). 'any' (not 'all') so deactivated rows
  // reach countByStatus and the Deactivated pill shows a real count — 'all'
  // would strip them and pin that pill to 0.
  const { counts, total: facetedTotal } = useMemo(
    () =>
      countByStatus(
        filterCompanies(data?.companies ?? [], {
          query: filter,
          status: 'any',
          contractorType: typeFilter,
          teamRegion: regionFilter,
          abnVerified,
        }),
      ),
    [data, filter, typeFilter, regionFilter, abnVerified],
  );
  // Deactivated companies are parked — the page's headline total reflects only
  // active companies (the Deactivated pill carries its own count).
  const grandTotal = useMemo(
    () => (data?.companies ?? []).filter((c) => c.status !== 'deactivated').length,
    [data],
  );

  const filtersActive =
    filter.trim() !== '' ||
    statusFilter !== 'all' ||
    typeFilter.length > 0 ||
    regionFilter.length > 0 ||
    abnVerified !== null;
  // One setSearchParams call clears all the filter params: react-router's
  // functional updater reads the *location's* current params, so chained calls
  // in the same tick would clobber each other.
  const clearFilters = () => {
    setFilter('');
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('status');
        p.delete('type');
        p.delete('region');
        p.delete('abn');
        return p;
      },
      { replace: true },
    );
  };

  const inviteMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ sent: boolean; expires_at: string }>(`/api/companies/${id}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });

  const [confirmAction, setConfirmAction] = useState<{
    company: Company;
    action: 'deactivate' | 'reactivate';
  } | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  // The modal's confirm button drives whichever mutation matches the pending action.
  const confirmMutation =
    confirmAction?.action === 'reactivate' ? reactivateMutation : deactivateMutation;

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="space-y-5">
      {/* Title row: heading + subtitle on the left, primary action top-right. */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Companies</h1>
          <p className="mt-1 text-sm text-slate-500" aria-live="polite">
            {filtersActive
              ? `${companies.length} of ${grandTotal} ${grandTotal === 1 ? 'company' : 'companies'}`
              : `${grandTotal} total ${grandTotal === 1 ? 'company' : 'companies'}`}
          </p>
        </div>
        <Btn onClick={() => setCreateOpen(true)}>+ New company</Btn>
      </div>

      {/* Status filter pills — replaces the old dropdown. */}
      <StatusFilterTabs
        value={statusFilter}
        counts={counts}
        total={facetedTotal}
        onChange={setStatusFilter}
      />

      {/* Companies card: the search row sits at the top of the same card as the
          table (per the screenshot), so it stays visible even when no rows match
          and the user needs to clear the search. */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                <NavIcon name="search" size={18} />
              </span>
              <TextInput
                className="pl-10"
                placeholder="Search company name, contact, company # or contract #…"
                aria-label="Search companies"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <MultiSelect
              label={companyInfoLabel(branding, 'contractorType')}
              values={typeValues.values}
              selected={typeFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('type', next)}
            />
            <MultiSelect
              label={companyInfoLabel(branding, 'teamRegion')}
              values={regionValues.values}
              selected={regionFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('region', next)}
            />
            <Select
              aria-label="ABN verification"
              value={searchParams.get('abn') ?? ''}
              onChange={(e) => setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                if (e.target.value) p.set('abn', e.target.value); else p.delete('abn');
                return p;
              }, { replace: true })}
            >
              <option value="">ABN: any</option>
              <option value="verified">ABN verified</option>
              <option value="unverified">ABN not verified</option>
            </Select>
          </div>
        </div>

        {companies.length === 0 ? (
          // Borderless empty block (mirrors EmptyState's content) so it sits
          // inside the card rather than drawing its own dashed border.
          <div className="p-12 text-center">
            <h3 className="text-base font-semibold text-slate-900">
              {filtersActive ? 'No matching companies' : 'No companies yet'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {filtersActive
                ? 'No companies match your current filters.'
                : 'Add a company to invite them to complete the questionnaire.'}
            </p>
            <div className="mt-4 flex justify-center">
              {filtersActive ? (
                <Btn variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Btn>
              ) : (
                <Btn onClick={() => setCreateOpen(true)}>+ New company</Btn>
              )}
            </div>
          </div>
        ) : (
          // The columns overflow narrow viewports and the card clips
          // (overflow-hidden), so let the table scroll sideways instead of
          // crushing the cells.
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Company</th>
                  <th className="px-6 py-3">Contact</th>
                  {/* Tenant-renamable labels (Settings), same source as the
                      filter dropdowns above. */}
                  <th className="px-6 py-3">{companyInfoLabel(branding, 'contractorType')}</th>
                  <th className="px-6 py-3">{companyInfoLabel(branding, 'teamRegion')}</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Progress</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={c.company_name} />
                        <Link
                          to={`/admin/companies/${c.id}`}
                          className="font-medium text-slate-900 hover:underline"
                        >
                          {c.company_name}
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.contact_name}
                      <div className="text-xs text-slate-500">{c.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.contractor_type ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.team_region ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <CompanyStatusBadge status={c.status} />
                    </td>
                    <td className="px-6 py-4 text-slate-700">{c.progress}%</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === 'deactivated' ? (
                          <IconActionBtn
                            label="Reactivate company"
                            icon="rotate-ccw"
                            onClick={() => setConfirmAction({ company: c, action: 'reactivate' })}
                          />
                        ) : (
                          <>
                            <IconActionBtn
                              label={c.invited_at ? 'Resend invite' : 'Send invite'}
                              icon="mail"
                              loading={
                                inviteMutation.isPending && inviteMutation.variables === c.id
                              }
                              onClick={() => inviteMutation.mutate(c.id)}
                            />
                            <IconActionBtn
                              label="Deactivate company"
                              icon="ban"
                              onClick={() => setConfirmAction({ company: c, action: 'deactivate' })}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inviteMutation.error && <ErrorMessage error={inviteMutation.error} />}
      {inviteMutation.isSuccess && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Invitation sent.
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          open
          title={confirmAction.action === 'deactivate' ? 'Deactivate company' : 'Reactivate company'}
          confirmLabel={confirmAction.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          danger={confirmAction.action === 'deactivate'}
          loading={confirmMutation.isPending}
          onConfirm={() => confirmMutation.mutate(confirmAction.company.id)}
          onClose={() => {
            setConfirmAction(null);
            deactivateMutation.reset();
            reactivateMutation.reset();
          }}
        >
          {confirmAction.action === 'deactivate' ? (
            <>
              Are you sure you want to deactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll be removed from the companies list and lose portal access. Their records
              are kept — you can reactivate them anytime.
            </>
          ) : (
            <>
              Are you sure you want to reactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll return to their previous status and regain portal access.
            </>
          )}
          {confirmMutation.error != null && (
            <div className="mt-3">
              <ErrorMessage error={confirmMutation.error} />
            </div>
          )}
        </ConfirmModal>
      )}
      <CreateCompanyModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// Compact icon-only action for table rows. Btn's sm padding is sized for text,
// so square it up with an important px override; the label reaches assistive
// tech via aria-label and sighted users via the title tooltip.
function IconActionBtn({
  label,
  icon,
  onClick,
  loading = false,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <Btn
      size="sm"
      variant="ghost"
      className="!px-2"
      aria-label={label}
      title={label}
      loading={loading}
      onClick={onClick}
    >
      {loading ? null : <NavIcon name={icon} size={16} />}
    </Btn>
  );
}

type CreateForm = {
  company_name: string;
  contact_name: string;
  email: string;
  contractor_type: string;
  company_number: string;
  contract_number: string;
  team_region: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  company_name: '',
  contact_name: '',
  email: '',
  contractor_type: '',
  company_number: '',
  contract_number: '',
  team_region: '',
};

function CreateCompanyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [inviteAfter, setInviteAfter] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const contractorTypes = contractorTypeOptions(branding);
  const teamRegions = teamRegionNames(useTeamRegions().data);

  function field<K extends keyof CreateForm>(key: K, value: CreateForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
      };
      for (const key of [
        'contractor_type',
        'company_number',
        'contract_number',
        'team_region',
      ] as const) {
        const trimmed = form[key].trim();
        if (trimmed.length > 0) payload[key] = trimmed;
      }
      const { company } = await adminApi.post<{ company: Company }>('/api/companies', payload);
      if (inviteAfter) {
        await adminApi.post(`/api/companies/${company.id}/invite`).catch((e) => {
          throw e instanceof ApiError ? e : new Error('Created, but invite failed');
        });
      }
      await qc.invalidateQueries({ queryKey: ['companies'] });
      setForm(EMPTY_CREATE_FORM);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New company"
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={submit} loading={submitting} disabled={!form.company_name || !form.email}>
            Create{inviteAfter ? ' & invite' : ''}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Company legal name" required>
            <TextInput
              value={form.company_name}
              onChange={(e) => field('company_name', e.target.value)}
            />
          </Field>
          <Field label="Contact name" required>
            <TextInput
              value={form.contact_name}
              onChange={(e) => field('contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact email" required>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => field('email', e.target.value)}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Pre-filled company information
          </div>
          <p className="mb-3 text-xs text-slate-500">
            These values appear pre-filled on the company&apos;s questionnaire when they accept the
            invite. All optional — leave blank if not applicable.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={companyInfoLabel(branding, 'contractorType')}
              hint={
                contractorTypes.length === 0
                  ? 'Add contractor types in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <Select
                value={form.contractor_type}
                onChange={(e) => field('contractor_type', e.target.value)}
                disabled={contractorTypes.length === 0}
              >
                <option value="">— Select —</option>
                {contractorTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={companyInfoLabel(branding, 'companyNumber')}>
              <TextInput
                value={form.company_number}
                onChange={(e) => field('company_number', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(branding, 'contractNumber')}>
              <TextInput
                value={form.contract_number}
                onChange={(e) => field('contract_number', e.target.value)}
              />
            </Field>
            <Field
              label={companyInfoLabel(branding, 'teamRegion')}
              hint={
                teamRegions.length === 0
                  ? 'Add team regions in Settings…'
                  : undefined
              }
            >
              <Select
                value={form.team_region}
                onChange={(e) => field('team_region', e.target.value)}
                disabled={teamRegions.length === 0}
              >
                <option value="">— Select —</option>
                {teamRegions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={inviteAfter}
            onChange={(e) => setInviteAfter(e.target.checked)}
          />
          Send invitation immediately
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
